<template>
  <div>
    <v-card-title>
      <span class="text-h5">Redac Process Templates</span>
      <v-spacer></v-spacer>
      <v-btn color="primary" @click="openAddDialog">
        <v-icon left>{{ icons.mdiPlus }}</v-icon>
        Add Template
      </v-btn>
    </v-card-title>
    <v-card-text>
      <div v-if="!loading && templates.length === 0" class="text-center py-5">
        <p>No templates found. Click "Add Template" to create one.</p>
      </div>
      <v-data-table
        v-else
        :headers="headers"
        :items="templates"
        :loading="loading"
        sort-by="is_default"
        :sort-desc="true"
        class="elevation-1"
      >
        <template slot="item.site_id" slot-scope="{ item }">
          {{ item.site_id }} - {{ getSiteName(item.site_id) }}
        </template>
        <template slot="item.is_default" slot-scope="{ item }">
          <v-chip v-if="item.is_default" color="success" small>Default</v-chip>
        </template>

        <template slot="item.is_shared" slot-scope="{ item }">
          <v-chip v-if="item.is_shared" color="primary" small>Shared</v-chip>
          <v-chip v-else color="grey" small>Private</v-chip>
        </template>

        <template slot="item.models" slot-scope="{ item }">
          <div class="text-caption">
            <div>Stage 1: {{ item.stages.stage1.model }}</div>
            <div>Stage 2: {{ item.stages.stage2.model }}</div>
            <div>Stage 3: {{ item.stages.stage3.model }}</div>
            <div>Stage 4: {{ item.stages.stage4.model }}</div>
          </div>
        </template>

        <template slot="item.image_is_own" slot-scope="{ item }">
          <v-chip v-if="item.image_is_own" color="success" small>Propre</v-chip>
          <v-chip v-else color="grey" small outlined>Hérité</v-chip>
        </template>

        <template slot="item.times_used" slot-scope="{ item }">
          {{ item.times_used || 0 }}
        </template>

        <template slot="item.last_used" slot-scope="{ item }">
          {{ formatDate(item.last_used) }}
        </template>

        <template slot="item.actions" slot-scope="{ item }">
          <!-- Restreint par site : un template global (sans site) se lit et se
               duplique, mais ne s'edite ni ne se supprime (le back refuse). -->
          <v-btn
            v-if="canWrite(item)"
            icon
            small
            title="Modifier"
            @click="openEditDialog(item)"
          >
            <v-icon small>{{ icons.mdiPencil }}</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            :title="
              isSiteRestricted && !item.site_id
                ? 'Dupliquer vers mon site'
                : 'Dupliquer'
            "
            @click="duplicateTemplate(item)"
          >
            <v-icon small>{{ icons.mdiContentDuplicate }}</v-icon>
          </v-btn>
          <v-btn
            v-if="canWrite(item)"
            icon
            small
            color="error"
            @click="confirmDelete(item)"
            :disabled="item.is_default"
          >
            <v-icon small>{{ icons.mdiDelete }}</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- Edit/Add Dialog -->
    <v-dialog v-model="showEditDialog" max-width="1200px" scrollable>
      <v-card>
        <v-card-title>
          <span class="headline">{{
            editingTemplate.id ? "Edit Template" : "Add Template"
          }}</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editingTemplate.name"
                  label="Template Name"
                  required
                  :rules="[(v) => !!v || 'Name is required']"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="editingTemplate.description"
                  label="Description (optional)"
                  rows="2"
                ></v-textarea>
              </v-col>
              <v-col cols="12" md="4">
                <site-filter
                  :initialValue="editingTemplate.site_id"
                  @valueChanged="updateSite"
                  :dependsOnSiteGroups="false"
                  :multiple="false"
                  :noUrl="true"
                  :zindex="zIndex.modale"
                  :siteListGetter="siteListGetter"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-checkbox
                  v-model="editingTemplate.is_default"
                  label="Set as Default"
                ></v-checkbox>
              </v-col>
            </v-row>
            <v-expansion-panels class="mb-6" accordion>
              <v-expansion-panel>
                <v-expansion-panel-header class="text-h8">
                  Prompt image
                  <span class="ml-2">
                    <v-chip x-small :color="imagePromptScopeColor" dark>
                      {{ imagePromptScopeLabel }}
                    </v-chip>
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-alert
                    v-if="!editingTemplate.id"
                    type="warning"
                    dense
                    outlined
                    class="mb-0"
                  >
                    Enregistrez d'abord le template : le prompt image s'attache à
                    un template existant.
                  </v-alert>

                  <template v-else>
                    <v-alert
                      v-if="imagePrompt.scope === 'template'"
                      type="success"
                      dense
                      outlined
                      class="mb-2"
                    >
                      Ce template a son <strong>propre</strong> prompt image. Le
                      modifier n'affecte aucun autre template.
                    </v-alert>
                    <v-alert
                      v-else-if="imagePrompt.scope === 'site'"
                      type="info"
                      dense
                      outlined
                      class="mb-2"
                    >
                      Prompt <strong>hérité du site</strong>, partagé par ses
                      autres templates. L'enregistrer ici en créera un propre à
                      ce template, sans toucher aux autres. Pour modifier le
                      prompt du site lui-même, passez par l'onglet
                      <strong>Image</strong> de cette page.
                    </v-alert>
                    <v-alert v-else type="warning" dense outlined class="mb-2">
                      Prompt <strong>global</strong>, commun à tous les sites sans
                      prompt propre. Il s'édite dans l'onglet
                      <strong>Image</strong> de cette page. L'enregistrer ici en
                      créera un propre à ce template.
                    </v-alert>

                    <v-alert
                      v-if="imagePrompt.archived"
                      type="info"
                      dense
                      outlined
                      class="mb-2"
                    >
                      Ce template a un prompt image <strong>archivé</strong>.
                      Le réactiver restaure <strong>son</strong> texte ; à
                      l'inverse, enregistrer ci-dessous écraserait ce texte par
                      le prompt hérité affiché.
                      <div class="mt-2">
                        <v-btn
                          color="primary"
                          x-small
                          outlined
                          :loading="imagePrompt.saving"
                          @click="restoreTemplateImagePrompt"
                        >
                          Réactiver le prompt archivé
                        </v-btn>
                        <v-btn
                          text
                          x-small
                          class="ml-2"
                          @click="showArchived = !showArchived"
                        >
                          {{ showArchived ? "Masquer" : "Voir" }} son contenu
                        </v-btn>
                      </div>
                      <v-textarea
                        v-if="showArchived"
                        :value="imagePrompt.archived.user_prompt"
                        rows="6"
                        outlined
                        readonly
                        hide-details
                        class="mt-3"
                      ></v-textarea>
                    </v-alert>

                    <v-alert
                      v-if="imagePrompt.loaded && !imagePromptHasVariable"
                      type="error"
                      dense
                      outlined
                      class="mb-2"
                    >
                      Ce prompt ne contient pas <code>{{ imagePromptVar }}</code
                      >. La scène déduite de l'article sera calculée, facturée,
                      puis <strong>ignorée</strong> : tous les articles de ce
                      template recevront la même image.
                    </v-alert>

                    <v-row class="pt-2">
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="imagePrompt.model_id"
                          :items="imageModelOptions"
                          label="Modèle de génération"
                          outlined
                          dense
                          hide-details
                          class="mb-4"
                          :disabled="imagePrompt.saving"
                        ></v-select>
                      </v-col>
                      <v-col cols="12" md="6" class="d-flex align-center">
                        <span class="text-caption grey--text">
                          Format : {{ imagePrompt.size || "—" }}
                          <template v-if="imagePrompt.template_name">
                            · source : {{ imagePrompt.template_name }}
                          </template>
                        </span>
                      </v-col>

                      <v-col cols="12">
                        <v-textarea
                          v-model="imagePrompt.user_prompt"
                          label="Prompt image du template"
                          rows="8"
                          outlined
                          hide-details
                          :disabled="imagePrompt.saving"
                          :placeholder="imagePromptVar"
                        ></v-textarea>
                      </v-col>

                      <v-col
                        v-if="imagePrompt.system_prompt"
                        cols="12"
                        class="pt-0"
                      >
                        <v-textarea
                          :value="imagePrompt.system_prompt"
                          label="Style et consignes de sécurité (hérité, non modifiable ici)"
                          rows="4"
                          outlined
                          readonly
                          hide-details
                          class="mt-3"
                        ></v-textarea>
                      </v-col>

                      <v-col cols="12" class="d-flex justify-end">
                        <v-btn
                          v-if="imagePrompt.scope === 'template'"
                          text
                          small
                          class="mr-2"
                          :loading="imagePrompt.saving"
                          @click="clearTemplateImagePrompt"
                        >
                          Revenir au prompt du site
                        </v-btn>
                        <v-btn
                          color="primary"
                          small
                          :loading="imagePrompt.saving"
                          @click="saveTemplateImagePrompt"
                        >
                          {{
                            imagePrompt.scope === "template"
                              ? "Enregistrer le prompt image"
                              : "Créer un prompt propre à ce template"
                          }}
                        </v-btn>
                      </v-col>
                    </v-row>
                  </template>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-tabs v-model="activeTab" class="mt-4">
              <v-tab>Stage 1: Analyze Sources</v-tab>
              <v-tab>Stage 2: Generate Content</v-tab>
              <v-tab>Stage 3: Generate Extras</v-tab>
              <v-tab>Stage 4: Generate Title</v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
              <v-tab-item
                v-for="stage in ['stage1', 'stage2', 'stage3', 'stage4']"
                :key="stage"
              >
                <v-container>
                  <v-row>
                    <v-col cols="12">
                      <v-select
                        v-model="editingTemplate.stages[stage].model"
                        :items="modelOptions"
                        label="Model"
                        @change="onModelChange(stage)"
                      ></v-select>
                    </v-col>
                  </v-row>

                  <v-row
                    v-if="
                      hasParameter(
                        editingTemplate.stages[stage].model,
                        'temperature'
                      )
                    "
                  >
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model.number="
                          editingTemplate.stages[stage].temperature
                        "
                        label="Temperature"
                        type="number"
                        min="0"
                        max="2"
                        step="0.1"
                      ></v-text-field>
                    </v-col>
                    <v-col
                      v-if="
                        hasParameter(
                          editingTemplate.stages[stage].model,
                          'top_p'
                        )
                      "
                      cols="12"
                      md="4"
                    >
                      <v-text-field
                        v-model.number="editingTemplate.stages[stage].top_p"
                        label="Top P"
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                      ></v-text-field>
                    </v-col>
                    <v-col
                      v-if="
                        hasParameter(
                          editingTemplate.stages[stage].model,
                          'top_k'
                        )
                      "
                      cols="12"
                      md="4"
                    >
                      <v-text-field
                        v-model.number="editingTemplate.stages[stage].top_k"
                        label="Top K"
                        type="number"
                        min="1"
                        step="1"
                        max="40"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row
                    v-else-if="
                      hasParameter(
                        editingTemplate.stages[stage].model,
                        'verbosity'
                      )
                    "
                  >
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="editingTemplate.stages[stage].verbosity"
                        :items="
                          getVerbosityOptions(
                            editingTemplate.stages[stage].model
                          )
                        "
                        label="Verbosity"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="editingTemplate.stages[stage].reasoning_effort"
                        :items="
                          getReasoningOptions(
                            editingTemplate.stages[stage].model
                          )
                        "
                        label="Reasoning Effort"
                        :disabled="
                          getReasoningOptions(
                            editingTemplate.stages[stage].model
                          ).length <= 1
                        "
                      ></v-select>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="editingTemplate.stages[stage].system_prompt"
                        label="System Prompt"
                        rows="8"
                        outlined
                        hide-details
                        class="mb-4"
                      ></v-textarea>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea
                        v-model="editingTemplate.stages[stage].user_prompt"
                        label="User Prompt"
                        rows="8"
                        outlined
                        hide-details
                      ></v-textarea>
                    </v-col>
                  </v-row>
                </v-container>
              </v-tab-item>
            </v-tabs-items>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeEditDialog">
            Cancel
          </v-btn>
          <v-btn color="primary" text @click="saveTemplate" :loading="saving">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      top
    >
      {{ snackbar.message }}
      <template slot="action">
        <v-btn text @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this template? This action cannot be
          undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="showDeleteDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" text @click="deleteTemplate" :loading="deleting">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "@axios";
import { mdiPencil, mdiDelete, mdiPlus, mdiContentDuplicate } from "@mdi/js";
import store from "@/store";
import SiteFilter from "@/components/common/filters/SiteFilter";
import { zIndex } from "@/utils/constants";
import { formatDateTime } from "@/utils/formatting";
import llprService from "@/services/llpr.service";

export default {
  name: "RedacProcessTab",

  components: {
    SiteFilter,
  },

  props: {
    siteId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      templates: [],
      loading: false,
      siteList: [],
      showEditDialog: false,
      showDeleteDialog: false,
      editingTemplate: this.getEmptyTemplate(),
      llprModels: [],
      deletingTemplate: null,
      openedRouteTemplateId: null,
      activeTab: 0,
      saving: false,
      deleting: false,
      // Litteral : Vue interpreterait la double accolade dans le template.
      imagePromptVar: "{{ image_prompt }}",
      // Prompt image servi pour le template edite. `scope` dit d'ou il vient
      // (template / site / global) : sans lui, un prompt herite serait
      // indiscernable d'un prompt propre et l'editer ecraserait celui du site.
      imagePrompt: this.getEmptyImagePrompt(),
      showArchived: false,
      icons: {
        mdiPencil,
        mdiDelete,
        mdiPlus,
        mdiContentDuplicate,
      },
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
      headers: [
        { text: "Name", value: "name" },
        { text: "Description", value: "description" },
        { text: "Site", value: "site_id" },
        { text: "Default", value: "is_default" },
        { text: "Shared", value: "is_shared" },
        { text: "Models", value: "models", sortable: false },
        { text: "Image", value: "image_is_own", sortable: false },
        { text: "Times Used", value: "times_used" },
        { text: "Last Used", value: "last_used" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      zIndex,
    };
  },

  computed: {
    // Modèles texte LLPR pour les stages 1-4 (exclut image, exclut inactifs)
    availableModels() {
      return this.llprModels.filter(
        (m) => !m.supports_image_generation && m.is_active !== false
      );
    },

    modelOptions() {
      return this.availableModels.map((m) => ({
        text: m.display_name,
        value: m.id,
      }));
    },

    // Le binding porte toujours un vrai modèle : "pas d'image" n'en est pas un,
    // ce choix se fait par article (toggle Aucune/Générer).
    imageModelOptions() {
      return this.llprModels
        .filter((m) => m.supports_image_generation && m.is_active !== false)
        .map((m) => ({ text: m.display_name, value: m.id }));
    },

    imagePromptScopeLabel() {
      return (
        {
          template: "propre au template",
          site: "hérité du site",
          global: "hérité du global",
        }[this.imagePrompt.scope] || "—"
      );
    },

    imagePromptScopeColor() {
      return (
        { template: "success", site: "info", global: "warning" }[
          this.imagePrompt.scope
        ] || "grey"
      );
    },

    imagePromptHasVariable() {
      return (this.imagePrompt.user_prompt || "").includes(this.imagePromptVar);
    },

    isSiteRestricted() {
      return this.$store.getters["common/getIsSiteRestricted"];
    },

    siteListGetter() {
      return this.isSiteRestricted
        ? "common/getAccessibleSiteList"
        : "common/getSiteList";
    },
  },

  watch: {
    siteId() {
      this.loadTemplates();
    },
    "$route.query.template_id"() {
      this.openTemplateFromRoute();
    },
  },

  async mounted() {
    await Promise.all([
      this.loadTemplates(),
      this.loadSiteList(),
      this.loadLlprModels(),
    ]);
  },

  methods: {
    getEmptyImagePrompt() {
      return {
        loaded: false,
        saving: false,
        binding_id: null,
        scope: null,
        user_prompt: "",
        system_prompt: "",
        model_id: null,
        size: null,
        template_name: null,
        // Prompt propre au template mais desactive : le signaler evite que
        // "enregistrer" ne l'ecrase avec le texte herite affiche.
        archived: null,
      };
    },

    async loadTemplateImagePrompt() {
      // Reinitialise avant de charger : sans ca le prompt du template precedent
      // resterait affiche si l'appel echoue ou si le template n'est pas encore
      // enregistre.
      this.imagePrompt = this.getEmptyImagePrompt();
      this.showArchived = false;
      const templateId = this.editingTemplate?.id;
      if (!templateId) return;
      try {
        const { data } = await axios.get(
          `/api/ai-factory/template-image-prompt/${templateId}`
        );
        this.imagePrompt = { ...this.imagePrompt, ...data, loaded: true };
      } catch (error) {
        console.error("Error loading template image prompt:", error);
      }
    },

    async saveTemplateImagePrompt() {
      const templateId = this.editingTemplate?.id;
      if (!templateId) return;
      this.imagePrompt.saving = true;
      try {
        const { data } = await axios.put(
          `/api/ai-factory/template-image-prompt/${templateId}`,
          {
            user_prompt: this.imagePrompt.user_prompt,
            model_id: this.imagePrompt.model_id,
          }
        );
        await this.loadTemplateImagePrompt();
        await this.loadTemplates();
        this.showSuccess(
          data.created_template_binding
            ? "Prompt image créé pour ce template (les autres ne sont pas affectés)"
            : "Prompt image du template mis à jour"
        );
      } catch (error) {
        this.showError(
          this.apiErrorMessage(error, "Failed to save template image prompt")
        );
      } finally {
        this.imagePrompt.saving = false;
      }
    },

    async restoreTemplateImagePrompt() {
      const templateId = this.editingTemplate?.id;
      if (!templateId) return;
      this.imagePrompt.saving = true;
      try {
        const { data } = await axios.post(
          `/api/ai-factory/template-image-prompt/${templateId}/restore`
        );
        await this.loadTemplateImagePrompt();
        await this.loadTemplates();
        this.showSuccess(
          data.has_image_prompt_variable
            ? "Prompt image du template réactivé"
            : "Prompt réactivé, mais il ne contient pas {{ image_prompt }}"
        );
      } catch (error) {
        this.showError(
          this.apiErrorMessage(error, "Failed to restore template image prompt")
        );
      } finally {
        this.imagePrompt.saving = false;
      }
    },

    async clearTemplateImagePrompt() {
      const templateId = this.editingTemplate?.id;
      if (!templateId) return;
      this.imagePrompt.saving = true;
      try {
        await axios.delete(
          `/api/ai-factory/template-image-prompt/${templateId}`
        );
        await this.loadTemplateImagePrompt();
        await this.loadTemplates();
        this.showSuccess("Ce template hérite à nouveau du prompt image du site");
      } catch (error) {
        this.showError(
          this.apiErrorMessage(error, "Failed to clear template image prompt")
        );
      } finally {
        this.imagePrompt.saving = false;
      }
    },

    async loadTemplates() {
      this.loading = true;
      try {
        const params = {};
        if (this.siteId) {
          params.site_id = this.siteId;
        } else {
          params.all_sites = true;
        }
        const response = await axios.get("/api/ai-factory/custom-templates", {
          params,
        });
        this.templates = response.data.templates || [];
        this.openTemplateFromRoute();
      } catch (error) {
        console.error("Error loading templates:", error);
        this.showError("Failed to load templates");
      } finally {
        this.loading = false;
      }
    },

    async loadSiteList() {
      try {
        this.siteList = await store.dispatch("common/getSiteList");
      } catch (error) {
        console.error("Error loading site list:", error);
        this.showError("Failed to load site list");
      }
    },

    async loadLlprModels() {
      try {
        const response = await llprService.getModels();
        this.llprModels = response.data || [];
      } catch (error) {
        console.error("Error loading LLPR models:", error);
        this.showError("Failed to load LLPR models");
      }
    },

    showSuccess(message) {
      this.snackbar = { show: true, message, color: "success" };
    },

    showError(message) {
      this.snackbar = { show: true, message, color: "error" };
    },

    apiErrorMessage(error, fallback) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail))
        return detail
          .map((d) => d?.msg)
          .filter(Boolean)
          .join(", ");
      return fallback || error?.message || "Unknown error";
    },

    // Helper pour récupérer la définition complète d'un modèle choisi (LLPR row)
    getModelDef(modelName) {
      return this.llprModels.find((m) => m.id === modelName);
    },

    // Métadonnées UI : dans LLPR elles sont sous default_params (cf. seed)
    getModelMeta(modelName) {
      const def = this.getModelDef(modelName);
      return (def && def.default_params) || {};
    },

    // Helper pour savoir si c'est un modèle type GPT-5 (basé sur les paramètres)
    isGpt5Family(modelName) {
      const meta = this.getModelMeta(modelName);
      if (Array.isArray(meta.parameters)) {
        return meta.parameters.includes("verbosity");
      }
      return modelName && modelName.startsWith("gpt-5");
    },

    hasParameter(modelName, paramName) {
      const meta = this.getModelMeta(modelName);
      const lowerName =
        typeof modelName === "string" ? modelName.toLowerCase() : "";
      if (!Array.isArray(meta.parameters)) {
        if (paramName === "temperature" || paramName === "top_p") {
          return modelName === "gpt-4o" || lowerName.startsWith("gemini");
        }
        if (paramName === "top_k") {
          return lowerName.startsWith("gemini");
        }
        if (paramName === "verbosity" || paramName === "reasoning_effort") {
          return this.isGpt5Family(modelName);
        }
        return false;
      }
      return meta.parameters.includes(paramName);
    },

    // Helper pour récupérer les options dynamiques
    getVerbosityOptions(modelName) {
      const meta = this.getModelMeta(modelName);
      return meta.verbosity_options || ["low", "medium", "high"];
    },

    getReasoningOptions(modelName) {
      const meta = this.getModelMeta(modelName);
      return (
        meta.reasoning_effort_options || ["minimal", "low", "medium", "high"]
      );
    },

    getSiteName(site_id) {
      const site = this.siteList.find((s) => s.id === site_id);
      return site?.name ?? "";
    },

    formatDate(dateString) {
      if (!dateString) return "Never";
      return formatDateTime(dateString);
    },

    getEmptyTemplate() {
      return {
        id: null,
        name: "",
        description: "",
        is_default: false,
        is_shared: false,
        site_id: "",
        stages: {
          stage1: {
            model: "gpt-5", // Défaut "sûr", sera ajusté au chargement
            system_prompt: "",
            user_prompt: "",
            temperature: null,
            top_p: null,
            top_k: null,
            verbosity: "medium",
            reasoning_effort: "medium",
          },
          stage2: {
            model: "gpt-4o",
            system_prompt: "",
            user_prompt: "",
            temperature: 0.4,
            top_p: 0.9,
            top_k: null,
            verbosity: null,
            reasoning_effort: null,
          },
          stage3: {
            model: "gpt-4o",
            system_prompt: "",
            user_prompt: "",
            temperature: 0.3,
            top_p: 0.9,
            top_k: null,
            verbosity: null,
            reasoning_effort: null,
          },
          stage4: {
            model: "gpt-4o",
            system_prompt: "",
            user_prompt: "",
            temperature: 0.3,
            top_p: 0.9,
            top_k: null,
            verbosity: null,
            reasoning_effort: null,
          },
        },
      };
    },

    openAddDialog() {
      this.editingTemplate = this.getEmptyTemplate();
      this.imagePrompt = this.getEmptyImagePrompt();
      this.activeTab = 0;
      this.showEditDialog = true;
    },

    openEditDialog(template) {
      this.editingTemplate = JSON.parse(JSON.stringify(template));
      this.activeTab = 0;
      this.showEditDialog = true;
      this.loadTemplateImagePrompt();
    },

    openTemplateFromRoute() {
      const templateId = Number(this.$route.query.template_id || 0);
      if (templateId <= 0) {
        this.openedRouteTemplateId = null;
        return;
      }
      if (this.openedRouteTemplateId === templateId) return;

      const template = this.templates.find(
        (item) =>
          Number(item.id) === templateId ||
          (Array.isArray(item.binding_ids) &&
            item.binding_ids.some((id) => Number(id) === templateId))
      );
      if (!template) return;

      this.openedRouteTemplateId = templateId;
      this.openEditDialog(template);
    },

    // La copie n'emporte PAS le prompt image : il appartient au binding du
    // template d'origine, pas a son JSON. La copie herite donc du site tant
    // qu'on ne lui en donne pas un propre.
    duplicateTemplate(template) {
      const copy = JSON.parse(JSON.stringify(template));
      copy.id = null;
      copy.name = "Copie de " + template.name;
      copy.is_default = false;
      copy.site_id = "";
      // Restreint par site : la copie d'un global ne peut vivre que sur un de
      // ses sites, on le pre-remplit (sinon le back refuse un template sans site).
      if (this.isSiteRestricted) {
        const accessible =
          this.$store.getters["common/getAccessibleSiteIds"] || [];
        copy.site_id = accessible.includes(template.site_id)
          ? template.site_id
          : accessible[0] || "";
      }
      this.editingTemplate = copy;
      this.imagePrompt = this.getEmptyImagePrompt();
      this.activeTab = 0;
      this.showEditDialog = true;
    },

    closeEditDialog() {
      this.showEditDialog = false;
      this.editingTemplate = this.getEmptyTemplate();
      this.imagePrompt = this.getEmptyImagePrompt();
    },

    onModelChange(stage) {
      const modelName = this.editingTemplate.stages[stage].model;
      const meta = this.getModelMeta(modelName);

      // Reset complet d'abord
      this.editingTemplate.stages[stage].temperature = null;
      this.editingTemplate.stages[stage].top_p = null;
      this.editingTemplate.stages[stage].top_k = null;
      this.editingTemplate.stages[stage].verbosity = null;
      this.editingTemplate.stages[stage].reasoning_effort = null;

      if (!Array.isArray(meta.parameters)) return; // Sécurité

      // Appliquer les défauts dynamiquement selon LLPR (default_params)
      if (meta.parameters.includes("temperature")) {
        this.editingTemplate.stages[stage].temperature =
          meta.default_temperature ?? 0.4;
        this.editingTemplate.stages[stage].top_p = meta.default_top_p ?? 0.9;
        if (meta.parameters.includes("top_k")) {
          this.editingTemplate.stages[stage].top_k = meta.default_top_k ?? 40;
        }
      }

      if (meta.parameters.includes("verbosity")) {
        this.editingTemplate.stages[stage].verbosity =
          meta.default_verbosity || "medium";
        // gpt-5.1 recevra "none", gpt-5 recevra "medium"
        this.editingTemplate.stages[stage].reasoning_effort =
          meta.default_reasoning_effort || "medium";
      }
    },

    async saveTemplate() {
      if (!this.editingTemplate.name) {
        this.showError("Template name is required");
        return;
      }
      const cleanedStages = this.stripMaxTokens(this.editingTemplate.stages);
      this.saving = true;
      try {
        const payload = {
          name: this.editingTemplate.name,
          description: this.editingTemplate.description,
          is_shared: false,
          is_default: this.editingTemplate.is_default,
          ...(this.editingTemplate.site_id
            ? { site_id: this.editingTemplate.site_id }
            : {}),
          stages: cleanedStages,
        };

        if (this.editingTemplate.id) {
          await axios.put(
            "/api/ai-factory/custom-templates/" + this.editingTemplate.id,
            payload
          );
          this.showSuccess("Template updated successfully");
        } else {
          await axios.post("/api/ai-factory/custom-templates", payload);
          this.showSuccess("Template created successfully");
        }
        await this.loadTemplates();
        this.closeEditDialog();
      } catch (error) {
        this.showError(this.apiErrorMessage(error, "Failed to save template"));
      } finally {
        this.saving = false;
      }
    },

    stripMaxTokens(stages) {
      const cleaned = {};
      Object.keys(stages || {}).forEach((key) => {
        const stage = { ...(stages[key] || {}) };
        if ("max_tokens" in stage) {
          delete stage.max_tokens;
        }
        cleaned[key] = stage;
      });
      return cleaned;
    },

    confirmDelete(template) {
      // ... (code inchangé)
      if (template.is_default) return;
      this.deletingTemplate = template;
      this.showDeleteDialog = true;
    },

    async deleteTemplate() {
      if (!this.deletingTemplate) return;
      this.deleting = true;
      try {
        await axios.delete(
          "/api/ai-factory/custom-templates/" + this.deletingTemplate.id
        );
        this.showSuccess("Template deleted successfully");
        await this.loadTemplates();
        this.showDeleteDialog = false;
        this.deletingTemplate = null;
      } catch (error) {
        this.showError(
          this.apiErrorMessage(error, "Failed to delete template")
        );
      } finally {
        this.deleting = false;
      }
    },

    updateSite(newSite) {
      this.editingTemplate.site_id = newSite;
    },

    canWrite(template) {
      return !this.isSiteRestricted || !!template.site_id;
    },
  },
};
</script>
